import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

/**
 * Convierte un path/URL de imagen en una URL absoluta usable por <img src=…>.
 * - "http(s)://…" o "data:…" → tal cual
 * - "/api/files/xxx" (o cualquier ruta absoluta) → BACKEND_URL + path (NO duplicar /api)
 * - "" / null → null
 */
export function resolveMedia(url) {
  if (!url) return null;
  if (/^(https?:|data:|blob:)/.test(url)) return url;
  const path = url.startsWith("/") ? url : `/${url}`;
  return `${BACKEND_URL}${path}`;
}

export const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
});

// Cliente sin auth (endpoints públicos: bootstrap kiosk/consulta precio)
export const publicApi = axios.create({
  baseURL: API_BASE,
});

api.interceptors.request.use((cfg) => {
  const token = localStorage.getItem("aipos_token");
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

// ---- Sesión persistente -------------------------------------------------
// El token de acceso dura horas; con el de refresco la sesión se renueva sola
// y la app del celular no vuelve a pedir el login.
export const TOKEN_KEY = "aipos_token";
export const REFRESH_KEY = "aipos_refresh";

export function guardarSesion({ access_token, refresh_token }) {
  if (access_token) localStorage.setItem(TOKEN_KEY, access_token);
  if (refresh_token) localStorage.setItem(REFRESH_KEY, refresh_token);
}

/** Borra solo la llave de acceso (el equipo queda bloqueado hasta la huella). */
export function olvidarAcceso() {
  localStorage.removeItem(TOKEN_KEY);
}

export function limpiarSesion() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_KEY);
  localStorage.removeItem("aipos_user");
}

let _refreshing = null;

// ¿El token de acceso ya venció? Un 401 con token vigente es un error del negocio
// (PIN equivocado, clave actual incorrecta), no una sesión caída.
function accesoVencido() {
  const t = localStorage.getItem(TOKEN_KEY);
  if (!t) return true;
  try {
    const { exp } = JSON.parse(atob(t.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")));
    return !exp || exp * 1000 <= Date.now() + 5000;
  } catch (_) {
    return true;
  }
}

async function renovarSesion() {
  const rt = localStorage.getItem(REFRESH_KEY);
  if (!rt) return null;
  // Una sola renovación aunque fallen varias peticiones a la vez
  if (!_refreshing) {
    _refreshing = axios
      .post(`${API_BASE}/auth/refresh`, { refresh_token: rt }, {
        headers: { Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY) || ""}` },
        withCredentials: true,
      })
      .then((r) => {
        guardarSesion(r.data);
        if (r.data?.user) localStorage.setItem("aipos_user", JSON.stringify(r.data.user));
        return r.data.access_token;
      })
      .catch((e) => {
        // Equipo con huella obligatoria: la sesión existe, falta el desbloqueo
        if (e?.response?.status === 403 && e?.response?.data?.detail === "BIO_LOCK_REQUIRED") {
          // La app móvil pide la huella; fuera de ella se vuelve a entrar con la contraseña
          if (window.location.pathname.startsWith("/movil")) window.dispatchEvent(new Event("aipos:bio-lock"));
          else limpiarSesion();
          return null;
        }
        // Solo se descarta el token cuando el servidor dijo que no sirve.
        // Sin red (celular en el metro) se conserva para reintentar después.
        if (e?.response?.status === 401) localStorage.removeItem(REFRESH_KEY);
        return null;
      })
      .finally(() => { _refreshing = null; });
  }
  return _refreshing;
}

const _featureToastAt = {};

api.interceptors.response.use(
  (r) => r,
  async (err) => {
    // Nunca dejar un objeto en `detail`: muchas vistas lo pintan directo y React reventaba
    // ("Objects are not valid as a React child").
    const d0 = err?.response?.data?.detail;
    if (d0 && typeof d0 === "object") {
      err.response.data.detail_data = d0;
      err.response.data.detail = d0.message || d0.detail || "No se pudo completar la operación";
    }
    if (err?.response?.status === 401) {
      const url = err.config?.url || "";
      // El login, el registro y la propia renovación no se reintentan
      const esEntrada = /\/auth\/(login|register|refresh|session|logout|reset-password|forgot)/.test(url);
      if (!esEntrada && (accesoVencido() || url.includes("/auth/me"))) {
        // Antes de cerrar la sesión se intenta renovarla una sola vez por petición
        if (!err.config?._reintento) {
          const nuevo = await renovarSesion();
          if (nuevo) {
            err.config._reintento = true;
            err.config.headers = { ...(err.config.headers || {}), Authorization: `Bearer ${nuevo}` };
            return api.request(err.config);
          }
          // La sesión no se pudo renovar: recién aquí se cierra
          limpiarSesion();
          localStorage.removeItem("aipos_company");
          localStorage.removeItem("aipos_store");
        }
        // Si ya se renovó y el endpoint igual responde 401, es un 401 del negocio
        // (PIN equivocado, clave actual incorrecta): la sesión sigue siendo válida.
      }
    }
    if (err?.response?.status === 402) {
      const d = err.response.data?.detail_data;
      const body = d || err.response.data;
      const code = body?.detail;
      if (code === "FEATURE_NOT_ENABLED" || code === "TRIAL_PAYMENT_METHOD_BLOCKED" || code === "STORE_LIMIT_REACHED") {
        // Un mismo bloqueo puede dispararse por varias peticiones de fondo: se avisa una vez.
        const key = body?.feature || code;
        const now = Date.now();
        if (!err.config?.silent402 && (!_featureToastAt[key] || now - _featureToastAt[key] > 15000)) {
          _featureToastAt[key] = now;
          import("sonner").then(({ toast }) => {
            toast.error(body?.feature_nombre || "Funcionalidad no habilitada", {
              description: body?.message,
              duration: 8000,
              action: { label: "Ver planes", onClick: () => { window.location.href = "/planes"; } },
            });
          });
        }
        return Promise.reject(err);
      }
    }
    if (err?.response?.status === 402 && err?.response?.data?.detail === "COMPANY_BLOCKED") {
      // Suscripción vencida: redirigir a planes
      if (!window.location.pathname.startsWith("/planes") &&
          !window.location.pathname.startsWith("/pago") &&
          !window.location.pathname.startsWith("/login")) {
        window.location.href = "/planes?blocked=1";
      }
    }
    return Promise.reject(err);
  }
);

export const subsApi = {
  list: () => api.get("/subscriptions").then((r) => r.data),
  cancellations: () => api.get("/subscriptions/cancellations").then((r) => r.data),
  requestCancel: (reason) => api.post("/subscriptions/cancel", { reason }).then((r) => r.data),
  revokeCancel: () => api.post("/subscriptions/cancel/revoke").then((r) => r.data),
  receiptUrl: (buy_order) => `${API_BASE}/subscriptions/receipt/${buy_order}`,
};

export const loyaltyApi = {
  getConfig: () => api.get("/loyalty/config").then((r) => r.data),
  saveConfig: (payload) => api.put("/loyalty/config", payload).then((r) => r.data),
  customers: () => api.get("/loyalty/customers").then((r) => r.data),
  lookup: (email) => api.get(`/loyalty/customer?email=${encodeURIComponent(email)}`).then((r) => r.data),
  transactions: (email) => api.get("/loyalty/transactions", { params: email ? { email } : {} }).then((r) => r.data),
  redeem: (payload) => api.post("/loyalty/redeem", payload).then((r) => r.data),
  publicLookup: (company_id, email) =>
    api.get(`/public/loyalty/${company_id}/customer?email=${encodeURIComponent(email)}`).then((r) => r.data),
};

export const hardwareApi = {
  listPublic: () => api.get("/hardware/products").then((r) => r.data),
  getPublic: (id) => api.get(`/hardware/products/${id}`).then((r) => r.data),
  checkout: (payload) => api.post("/hardware/checkout", payload).then((r) => r.data),
  orderByBuy: (buy_order) => api.get(`/hardware/orders/${buy_order}`).then((r) => r.data),
  adminList: () => api.get("/hardware/admin/products").then((r) => r.data),
  adminCreate: (p) => api.post("/hardware/admin/products", p).then((r) => r.data),
  adminUpdate: (id, p) => api.put(`/hardware/admin/products/${id}`, p).then((r) => r.data),
  adminDelete: (id) => api.delete(`/hardware/admin/products/${id}`).then((r) => r.data),
  adminOrders: () => api.get("/hardware/admin/orders").then((r) => r.data),
  adminOrderStatus: (id, status) => api.put(`/hardware/admin/orders/${id}/status`, { status }).then((r) => r.data),
  adminOrderReenviar: (id, email) => api.post(`/hardware/admin/orders/${id}/reenviar-confirmacion`, { email }).then((r) => r.data),
  notifyGet: () => api.get("/hardware/admin/notify-emails").then((r) => r.data),
  notifySet: (emails) => api.put("/hardware/admin/notify-emails", { emails }).then((r) => r.data),
  notifyTest: () => api.post("/hardware/admin/notify-emails/test").then((r) => r.data),
};

export const contestsApi = {
  list: () => api.get("/contests").then((r) => r.data),
  create: (p) => api.post("/contests", p).then((r) => r.data),
  update: (id, p) => api.put(`/contests/${id}`, p).then((r) => r.data),
  remove: (id) => api.delete(`/contests/${id}`).then((r) => r.data),
  vouchers: (contest_id) => api.get("/contests/vouchers", { params: contest_id ? { contest_id } : {} }).then((r) => r.data),
};

export const authApi = {
  register: (data) => api.post("/auth/register", data).then((r) => r.data),
  login: (data) => api.post("/auth/login", data).then((r) => r.data),
  me: () => api.get("/auth/me").then((r) => r.data),
  logout: () => api.post("/auth/logout", { refresh_token: localStorage.getItem(REFRESH_KEY) || null }).then((r) => r.data),
  emergentSession: (session_id) => api.post("/auth/session", { session_id }).then((r) => r.data),
};

export const mfaApi = {
  status: () => api.get("/auth/mfa/status").then((r) => r.data),
  start: (setup_token) => api.post("/auth/mfa/enroll/start", setup_token ? { setup_token } : {}).then((r) => r.data),
  confirm: (code, setup_token) => api.post("/auth/mfa/enroll/confirm", { code, ...(setup_token ? { setup_token } : {}) }).then((r) => r.data),
  loginCode: (pending_token, code) => api.post("/auth/login/mfa", { pending_token, code }).then((r) => r.data),
  regenerate: (code) => api.post("/auth/mfa/recovery/regenerate", { code }).then((r) => r.data),
};

export const clp = (n) =>
  new Intl.NumberFormat("es-CL", { style: "currency", currency: "CLP", maximumFractionDigits: 0 }).format(
    Number(n || 0)
  );

export const money = (n) =>
  new Intl.NumberFormat("es-CL", { maximumFractionDigits: 0 }).format(Number(n || 0));

// El SII acepta máximo 60 líneas de detalle por documento tributario
export const MAX_LINEAS_DTE = 60;
export const AVISO_LIMITE_LINEAS = `Llegaste a ${MAX_LINEAS_DTE} productos distintos, el máximo que acepta el SII por documento. Emite este documento y continúa con el resto en uno nuevo.`;
